import * as React from 'react';
import * as _ from 'lodash';
import { MathFunctions } from '../../functions/math';
import { IFullPaginationOptions } from './pagination';

interface IPageNumber {
    number: number;
    active: boolean;
}

export interface IPageButton extends IPageNumber {
    isLeftMost: boolean;
    isRightMost: boolean;
    isEllipsis: boolean;
    numberOfPages: number;
}

export interface IPagingProps {
    numberOfButtonsToShow: number;
    paging: IFullPaginationOptions;
    onPageSelect(page: number): void;
}

export interface IPageButtonProps {
    page: IPageButton;
    key: any;
    onPageSelect(page: number): void;
    queryCurrentPage?: any;
    pageSelectProps?: Function;
}

/// <summary>
/// Individual page buttons
/// </summary>
export function PageButton({ page, onPageSelect, queryCurrentPage, pageSelectProps }: IPageButtonProps) {
    if (queryCurrentPage) {
        pageSelectProps(queryCurrentPage);
    }

    /// <summary>
    /// Determines CSS class names for the button
    /// </summary>
    /// <returns>Css classes</returns>
    const GetClasses = () => {
        const activeOrEllipsis = page.active ? 'selected '
            : page.isEllipsis ? 'ellipsis '
                : '';

        return activeOrEllipsis + 'un-button adage-search-pagination-link ' + 'adage-search-pagination-buttons-item-' + GetContent().toString();
    };

    /// <summary>
    /// Determines text content to display for the button
    /// </summary>
    /// <returns>Content text</returns>
    const GetContent = () => {
        return page.isEllipsis ? '...'
            : page.isLeftMost ? 1
                : page.isRightMost ? page.numberOfPages
                    : page.number;
    };

    const GetAriaContent = () => {
        return page.active ? "Current Page, Page" : "Page";
    };

    return (
        <li className="adage-search-pagination-list-item" data-id="pageLinks">
            <button className={GetClasses()} onClick={() => page.isEllipsis || page.active ? false : onPageSelect(page.number)}>
                <span className="visually-hidden">
                    {GetAriaContent()}
                </span>
                <span dangerouslySetInnerHTML={{ __html: GetContent().toString() }} />
            </button>
        </li>
    );
}

/// <summary>
/// Creates and renders collection of page buttons
/// </summary>
export function Paging({ numberOfButtonsToShow, paging, onPageSelect }: IPagingProps) {
    /// <summary>
    /// Creates collection of page buttons to render
    /// </summary>
    /// <returns>Page buttons</returns>
    const GetPages = (): IPageButton[] => {
        const numberOfPages = paging.totalPages;
        const pagesMiddle = Math.ceil(numberOfButtonsToShow / 2);
        //Create list of page number objects
        var pageNumbers = [];
        for (var i = 0; i < (numberOfPages); i++) {
            pageNumbers[i] = { number: i + 1, active: (i + 1) === paging.pageNumber } as IPageNumber;
        }

        //Filter list to only the numbers we will display
        const centered = MathFunctions.getRangeCenteredAt(paging.pageNumber, numberOfButtonsToShow, pageNumbers);

        return centered
            .map((page, i) => {
                //Calculate page-specific attributes
                const isLeftMost = i === 0 && page.number !== 1;
                const isRightMost = page.number !== numberOfPages && page.number !== (numberOfPages - 1) && (i === centered.length - 1);

                const isLeftEllipsis = ((numberOfPages > numberOfButtonsToShow) && (paging.pageNumber > pagesMiddle)) && i === 1;
                const isRightEllipsis = ((numberOfPages > numberOfButtonsToShow) && ((numberOfPages - paging.pageNumber) > pagesMiddle)) && i === centered.length - 2;
                const isEllipsis = isLeftEllipsis || isRightEllipsis;

                const number = isLeftMost ? 1
                    : isRightMost ? numberOfPages
                        : page.number;

                return _.assign(page, {
                    isLeftMost,
                    isRightMost,
                    number,
                    numberOfPages,
                    isEllipsis
                }) as IPageButton;
            });
    };

    const renderPages = (page: IPageButton, i: number) => {
        return <PageButton
            page={page}
            key={i}
            onPageSelect={onPageSelect} />;
    };

    return (<>{GetPages().map(renderPages)}</>);
};
